import styled from 'styled-components'
import { Handle } from '@xyflow/react'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

// Container for the storyboard node - uses neutral theme colors
// Width accommodates 4 panels in a single row with captions
// No container padding - header and content areas handle their own padding (matches BaseNode)
export const StoryboardNodeContainer = styled.div<{ $isGenerating?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--color-node-bg);
  border: 1px solid var(--color-border-dark);
  border-radius: 8px;
  width: 1300px;
  box-shadow: var(--shadow-node);
  cursor: ${(props) => (props.$isGenerating ? 'default' : 'pointer')};
  transition: all 0.15s ease;

  ${(props) =>
    props.$isGenerating &&
    `
        animation: pulse 2s ease-in-out infinite;
    `}

  &:hover {
    border-color: var(--color-text-primary);
    box-shadow: var(--shadow-node-hover);
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
  }
`

// Header container - matches BaseNode's HeaderContainer
export const StoryboardHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  background-color: transparent;
  border-bottom: 1px solid var(--color-node-header-border);
  cursor: inherit;
`

// Header row - matches BaseNode's HeaderRow
export const StoryboardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

// Icon and title group with zoom scaling - matches BaseNode's IconTitleGroup
export const StoryboardIconTitleGroup = styled.div<{ $zoomScale?: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  transform: scale(${(props) => Math.min(props.$zoomScale ?? 1, 1.3) * 0.85});
  transform-origin: left center;
  flex-shrink: 0;
`

// Category title - matches BaseNode's CategoryTitle
export const StoryboardCategoryTitle = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  line-height: 1.2;
  color: var(--color-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

// Subheading - matches BaseNode's SubheadingContainer
export const StoryboardSubheading = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin-top: 4px;
  line-height: 1.3;
`

// Content title - shows the specific storyboard name
export const StoryboardContentTitle = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  padding: 8px 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const StoryboardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  flex-shrink: 0;
`

// Single row grid of 4 storyboard panels with captions
export const StoryboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 0 12px 12px;
`

// Wrapper for each panel containing image and caption
export const StoryboardPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const StoryboardPanel = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  border-radius: 6px;
  background: var(--color-surface-secondary);
`

export const StoryboardPanelPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 6px;
  background: var(--color-surface-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.h3.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
`

// Frame title (e.g., "The Situation", "The Struggle")
export const StoryboardFrameTitle = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.2;
`

// Caption text below each panel
export const StoryboardCaption = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.regular};
  color: var(--color-text-primary);
  line-height: 1.35;
`

// Invisible target handle for receiving edge from parent idea
export const StoryboardTargetHandle = styled(Handle)`
  opacity: 0;
  width: 24px;
  height: 24px;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

// ============================================================================
// COLLAPSED VIEW STYLES
// ============================================================================

// Wrapper for collapsed view - sets CSS variable for zoom-independent scaling
export const CollapsedWrapper = styled.div.attrs<{ $zoomScaleRaw: number }>((props) => ({
  style: {
    '--zoom-scale-raw': props.$zoomScaleRaw,
  } as React.CSSProperties,
}))`
  position: relative;
  display: inline-block;
  text-align: left;
`

// Fixed size container for stable handle positioning in collapsed view
export const CollapsedViewWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 32px;
  height: 32px;
  z-index: 1;
`

// Circular icon node for collapsed view - uses neutral theme colors
export const CollapsedCircle = styled.div<{ $isGenerating?: boolean }>`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scale(var(--zoom-scale-raw, 1));
  transform-origin: left center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-node-bg);
  border: 2px solid var(--color-text-primary);
  box-shadow: var(--shadow-collapsed);
  cursor: ${(props) => (props.$isGenerating ? 'default' : 'pointer')};
  color: var(--color-text-primary);

  ${(props) =>
    props.$isGenerating &&
    `
        animation: pulse 2s ease-in-out infinite;
    `}

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`

// Fixed-position target handle for collapsed view
// pointer-events: none !important to override ReactFlow's .connectionindicator { pointer-events: all }
export const CollapsedTargetHandle = styled(Handle)`
  opacity: 0;
  width: 16px;
  height: 16px;
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none !important;
`

// ============================================================================
// GENERATING STATE STYLES
// ============================================================================

// Container for the generating animation
export const GeneratingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  gap: 12px;
`

// Message displayed during generation
export const GeneratingMessage = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-align: center;
`

// ============================================================================
// WRAPPER STYLES
// ============================================================================

// Wrapper providing positioning context and zoom-scale CSS variable
// (mirrors BaseNode's NodeWrapper — consumed by labels, validation button, etc.)
export const StoryboardWrapper = styled.div.attrs<{ $zoomScale: number }>((props) => ({
  style: {
    '--zoom-scale': props.$zoomScale,
  } as React.CSSProperties,
}))`
  position: relative;
  display: inline-block;
  text-align: left;
`

// ============================================================================
// HEADER ACTIONS STYLES
// ============================================================================

// Container for header action buttons (e.g., triple dot menu)
export const HeaderActionsGroup = styled.div<{ $zoomScale?: number }>`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  transform: scale(${(props) => Math.min(props.$zoomScale ?? 1, 1.8)});
  transform-origin: right center;
`
